// src/components/share/attendance-card.tsx
"use client"

import React from "react"
import {
  Zona,
  compararPorZona,
  compiteEnLaTabla,
  porcentajeDeDescenso,
} from "utils/zonas"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AttendanceItem {
  id: string | number
  nombre: string
  asistencias: number
  partialTotal: number
  /** Server-calculated Tailwind color class, e.g. "bg-green-200", "bg-yellow-200", "bg-red-300", "bg-gray-400" */
  color?: string
  /** Server-calculated percentage as a string, e.g. "85" */
  porcentaje?: string
  /** Fecha de baja: un jugador dado de baja no pelea el último puesto. */
  deletedAt?: string | null
}

export interface AttendanceCardProps {
  /** Title shown in the header – defaults to "Asistencias" */
  title?: string
  /** Season label shown next to the title */
  season?: string
  /** Date string shown below the title */
  updatedAt?: string
  /** Sorted list of players with attendance data */
  resumen: AttendanceItem[]
  /** Cantidad de eventos de tipo "Fútbol" de la temporada */
  totalPartidos?: number
  /** Cantidad de eventos de tipo "Peña" de la temporada */
  totalPenas?: number
  isExporting?: boolean
}

// ---------------------------------------------------------------------------
// Paleta
//
// Todo va en hex explícito y sin `backdrop-filter`, `filter` ni degradados:
// html-to-image no resuelve esas propiedades, así que lo que se veía en la
// vista previa no era lo que salía en la imagen.
// ---------------------------------------------------------------------------

const GROUND = "#ffffff"
const GROUND_LIFTED = "#f5f5f7"
const LINE = "#e4e4e8"
const TEXT = "#1a1a20"
const MUTED = "#5b5b66"
const DIM = "#8c8c96"
const TRACK = "#eeeef1"

/** Color de la franja y de la barra: son formas, les alcanza con 3:1. */
const COLOR_ZONA: Record<Zona, string> = {
  alta: "#189a48",
  media: "#cf9a12",
  descenso: "#d1453f",
  "sin-datos": "#c2c4ca",
}

/** El rótulo "Descenso" es texto chico: necesita más contraste que la franja. */
const COLOR_ROTULO_DESCENSO = "#b02b26"
const LINEA_DESCENSO = "#f0d6d4"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pct(item: AttendanceItem): number {
  if (item.partialTotal === 0) return 0
  return Math.round((item.asistencias / item.partialTotal) * 100)
}

function formatPct(item: AttendanceItem): string {
  if (item.partialTotal === 0) return "—"
  // Prefer server-calculated percentage
  if (item.porcentaje !== undefined) return `${item.porcentaje}%`
  return `${pct(item)}%`
}

/** "1 partido" / "8 partidos" */
function pluralize(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`
}

/**
 * Zona del jugador.
 *
 * Usa el `color` que calcula el servidor cuando está disponible (mismos
 * umbrales de 75% y 25% que la tabla de la app) y si no lo deriva del
 * porcentaje. Un jugador sin eventos no tiene zona.
 */
function colorCategory(item: AttendanceItem): Zona {
  if (item.partialTotal === 0) return "sin-datos"

  if (item.color) {
    if (item.color.includes("green")) return "alta"
    if (item.color.includes("yellow")) return "media"
    if (item.color.includes("gray")) return "sin-datos"
    if (item.color.includes("red")) return "descenso"
  }

  const p = pct(item)
  if (p >= 75) return "alta"
  if (p >= 25) return "media"
  return "descenso"
}

const datosDescensoDe = (item: AttendanceItem) => ({
  porcentaje: pct(item),
  totalEventos: item.partialTotal,
  dadoDeBaja: Boolean(item.deletedAt) || Boolean(item.color?.includes("gray")),
})

/** Recorte por JS: html-to-image no aplica text-overflow. */
function truncateName(name: string, maxLen: number): string {
  if (name.length <= maxLen) return name
  return name.slice(0, maxLen - 1).trimEnd() + "…"
}

// ---------------------------------------------------------------------------
// Fila
// ---------------------------------------------------------------------------

type PlayerRowProps = {
  item: AttendanceItem
  rank: number
  isExporting?: boolean
  /** El primero de la tabla se levanta un tono. */
  destacado?: boolean
}

function PlayerRow({ item, rank, isExporting = false, destacado = false }: PlayerRowProps) {
  const zona = colorCategory(item)
  const percentage = pct(item)
  const color = COLOR_ZONA[zona]

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "3px 26px minmax(0,1fr) minmax(90px,2fr) 52px 56px",
        alignItems: "center",
        columnGap: "12px",
        padding: "9px 14px 9px 0",
        backgroundColor: destacado ? GROUND_LIFTED : "transparent",
        borderRadius: "6px",
      }}
    >
      {/* Franja de zona: el único color de la fila */}
      <div
        style={{
          width: "3px",
          height: "22px",
          borderRadius: "999px",
          backgroundColor: color,
        }}
      />

      {/* Posición */}
      <span
        style={{
          color: DIM,
          fontSize: "13px",
          fontWeight: 700,
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {rank}
      </span>

      {/* Nombre */}
      <span
        style={{
          color: TEXT,
          fontSize: "16px",
          fontWeight: 600,
          letterSpacing: "-0.01em",
          minWidth: 0,
          whiteSpace: "nowrap",
        }}
      >
        {truncateName(item.nombre, 14)}
      </span>

      {/* Barra */}
      <div>
        <div
          style={{
            height: "8px",
            borderRadius: "999px",
            backgroundColor: TRACK,
            overflow: "hidden",
          }}
        >
          {/*
            data-progress lo lee el exportador para convertir el ancho
            porcentual en píxeles, y data-bar-height para devolverle el alto
            que la normalización de estilos le saca.
          */}
          <div
            data-progress={percentage}
            data-bar-height="8"
            className="progress-inner"
            style={{
              height: "100%",
              width: percentage === 0 ? 0 : `${Math.max(percentage, 2)}%`,
              transition: isExporting ? "none" : "width 0.8s ease",
              borderRadius: 999,
              backgroundColor: color,
            }}
          />
        </div>
      </div>

      {/* Porcentaje */}
      <span
        style={{
          color: TEXT,
          fontSize: "16px",
          fontWeight: 700,
          textAlign: "right",
          whiteSpace: "nowrap",
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.02em",
        }}
      >
        {formatPct(item)}
      </span>

      {/* Presentes / total */}
      <span
        style={{
          color: DIM,
          fontSize: "13px",
          fontWeight: 500,
          textAlign: "right",
          whiteSpace: "nowrap",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {item.asistencias}/{item.partialTotal}
      </span>
    </div>
  )
}

/** Aire entre tramos de color, sin rótulo. */
function EspacioEntreTramos() {
  return <div style={{ height: "10px" }} />
}

/**
 * Corte del descenso: marca el último puesto de la tabla, no la franja de
 * porcentaje bajo. Mismo criterio que la app.
 */
function SeparadorDescenso() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "14px 14px 6px 0",
      }}
    >
      <span
        style={{
          color: COLOR_ROTULO_DESCENSO,
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
        }}
      >
        Descenso
      </span>
      <span
        style={{
          flex: 1,
          height: "1px",
          backgroundColor: LINEA_DESCENSO,
        }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AttendanceCard({
  title = "Asistencias",
  season = "Temporada 2026",
  updatedAt,
  resumen,
  totalPartidos,
  totalPenas,
  isExporting = false,
}: AttendanceCardProps) {
  // Por zona y, dentro de cada zona, por porcentaje descendente: así cada
  // zona es un bloque contiguo y su rótulo aparece una sola vez.
  const sorted = [...resumen].sort(compararPorZona(colorCategory, pct))

  // Último puesto de la tabla, con sus empates
  const porcentajeDescenso = porcentajeDeDescenso(sorted, datosDescensoDe)
  const estaEnDescenso = (item: AttendanceItem) =>
    porcentajeDescenso !== null &&
    compiteEnLaTabla(datosDescensoDe(item)) &&
    pct(item) === porcentajeDescenso

  // Contadores del encabezado: partidos y peñas van separados.
  // Si no llegan los conteos por tipo, caemos al total de eventos (comportamiento anterior).
  const counters: string[] = []
  if (totalPartidos) counters.push(pluralize(totalPartidos, "partido", "partidos"))
  if (totalPenas) counters.push(pluralize(totalPenas, "peña", "peñas"))
  if (counters.length === 0) {
    const totalGames =
      totalPartidos === undefined && totalPenas === undefined
        ? sorted.reduce((max, item) => Math.max(max, item.partialTotal), 0)
        : 0
    counters.push(pluralize(totalGames, "partido", "partidos"))
  }

  return (
    <div
      className="w-full mx-auto select-none"
      style={{
        backgroundColor: GROUND,
        fontFamily: '"DM Sans", sans-serif',
        padding: "26px 22px 22px",
      }}
    >
      {/* Encabezado */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "16px",
          paddingBottom: "18px",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p
            style={{
              color: MUTED,
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              margin: 0,
            }}
          >
            {title}
          </p>
          <h2
            style={{
              color: TEXT,
              fontSize: "27px",
              fontWeight: 700,
              letterSpacing: "-0.03em",
              lineHeight: 1.1,
              margin: "5px 0 0",
            }}
          >
            {season}
          </h2>
        </div>

        <p
          style={{
            color: DIM,
            fontSize: "13px",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            margin: "2px 0 0",
          }}
        >
          GRUN
        </p>
      </div>

      {/* Metadatos */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          borderTop: `1px solid ${LINE}`,
          borderBottom: `1px solid ${LINE}`,
          padding: "10px 0",
          marginBottom: "6px",
        }}
      >
        <span style={{ color: MUTED, fontSize: "13px", fontWeight: 500 }}>
          {counters.join(" · ")}
        </span>
        {updatedAt && (
          <span style={{ color: DIM, fontSize: "12px", fontWeight: 500 }}>
            Al {updatedAt}
          </span>
        )}
      </div>

      {/* Tabla */}
      <div style={{ paddingTop: "6px" }}>
        {sorted.map((item, idx) => {
          const anterior = idx > 0 ? sorted[idx - 1] : null
          const abreTramo =
            colorCategory(item) !== (anterior ? colorCategory(anterior) : null)
          const abreDescenso =
            estaEnDescenso(item) && !(anterior && estaEnDescenso(anterior))

          return (
            <React.Fragment key={item.id}>
              {/* El rótulo del descenso gana sobre el simple cambio de tramo */}
              {abreDescenso ? (
                <SeparadorDescenso />
              ) : abreTramo && idx > 0 ? (
                <EspacioEntreTramos />
              ) : null}
              <PlayerRow
                item={item}
                rank={idx + 1}
                isExporting={isExporting}
                destacado={idx === 0}
              />
            </React.Fragment>
          )
        })}
      </div>

      {/* Pie */}
      <div
        style={{
          borderTop: `1px solid ${LINE}`,
          marginTop: "16px",
          paddingTop: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
        }}
      >
        <span style={{ color: DIM, fontSize: "12px", fontWeight: 500 }}>
          {pluralize(sorted.length, "jugador", "jugadores")}
        </span>
      </div>
    </div>
  )
}
